import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../lib/useAuth'
import { MapPin, ShieldAlert, X as XIcon, AlertTriangle, FileCheck, MessageSquare } from 'lucide-react'

// ANU-89 — Command Center / Referee tab body for war crime reports.
//
// mode='gm'  → full resolution UI (DISMISS / WARN / PENALIZE + penalty input)
// mode='iga' → ACKNOWLEDGE + ADD NOTE only (no DISMISS/WARN/PENALIZE)
//
// Subscribes to realtime INSERT on war_crimes so the badge + list bumps
// without a refresh. Each card renders the reporter→accused callsigns, an
// incident-type chip (color-coded), the timestamp, an external GPS link
// when geo was captured, the description, an optional photo thumbnail
// (tap → lightbox), and a status badge.
//
// On PENALIZE: writes resolution to war_crimes AND decrements the
// accused's session_points on event_players in a follow-up mutation.

const TYPE_LABELS = {
  team_kill: 'TEAM KILL',
  unsafe_play: 'UNSAFE PLAY',
  hit_dispute: 'HIT DISPUTE',
  cheating: 'CHEATING',
  spawn_camping: 'SPAWN CAMPING',
  other: 'OTHER',
}

const TYPE_COLORS = {
  team_kill: '#ff2a2a',
  unsafe_play: '#ff9a3d',
  hit_dispute: '#ffcc33',
  cheating: '#b380ff',
  spawn_camping: '#ff6a3d',
  other: '#8aa090',
}

const STATUS_COLORS = {
  pending: '#ffcc33',
  reviewed: '#4dd6c2',
  dismissed: '#5a6a60',
  penalized: '#ff2a2a',
  escalated: '#ff6a3d',
}

const S = {
  panel: { background: 'rgba(28, 38, 38, .55)', border: '1px solid rgba(140, 180, 130, .18)', padding: 14 },
  card: { padding: 12, background: 'rgba(0,0,0,.3)', border: '1px solid rgba(140,180,130,.15)', display: 'flex', flexDirection: 'column', gap: 6 },
  pill: (color) => ({ display: 'inline-block', padding: '2px 8px', background: `${color}1a`, border: `1px solid ${color}55`, color, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }),
  status: (color) => ({ display: 'inline-block', padding: '2px 8px', background: `${color}22`, border: `1px solid ${color}66`, color, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase' }),
  label: { color: '#8aa090', fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', fontFamily: "'Rajdhani', sans-serif", fontWeight: 600 },
  body: { color: '#e0e8de', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", overflowWrap: 'anywhere' },
  muted: { color: '#5a6a60', fontSize: 11, fontFamily: "'JetBrains Mono', monospace" },
  input: { width: '100%', background: 'rgba(0,0,0,.4)', color: '#e0e8de', border: '1px solid rgba(140,180,130,.25)', padding: '8px 10px', fontFamily: 'inherit', fontSize: 13, outline: 'none' },
  btn: (color) => ({ flex: 1, minHeight: 40, padding: '8px 10px', background: `${color}14`, border: `1px solid ${color}55`, color, fontFamily: "'Rajdhani', sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }),
}

export default function WarCrimesTab({ eventId, mode = 'gm' }) {
  const { user } = useAuth()
  const [rows, setRows] = useState([])
  const [draft, setDraft] = useState({}) // per-row: { notes, penalty }
  const [busy, setBusy] = useState({})
  const [lightbox, setLightbox] = useState(null)
  const [signedPhotos, setSignedPhotos] = useState({}) // path → url

  const load = async () => {
    if (!eventId) return
    const { data } = await supabase
      .from('war_crimes')
      .select(`
        id, event_id, reporter_id, accused_id, incident_type, description,
        photo_url, photo_storage_path, geo_lat, geo_lng, geo_accuracy_m,
        status, gm_notes, penalty_points, resolved_at, resolved_by, created_at,
        reporter:reporter_id (id, callsign, display_name),
        accused:accused_id (id, callsign, display_name)
      `)
      .eq('event_id', eventId)
      .order('created_at', { ascending: false })
      .limit(200)
    setRows(data || [])
  }

  useEffect(() => {
    if (!eventId) return
    load()
    const channel = supabase
      .channel(`war-crimes-${eventId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'war_crimes', filter: `event_id=eq.${eventId}` },
        () => load())
      .subscribe()
    return () => { try { supabase.removeChannel(channel) } catch { /* ignore */ } }
  }, [eventId])

  // Re-sign photo paths as cards mount. The photo_url snapshot from the
  // upload has a 1hr TTL; for anything older we need a fresh signed URL.
  useEffect(() => {
    const missing = rows.filter(r => r.photo_storage_path && !signedPhotos[r.photo_storage_path])
    if (missing.length === 0) return
    let cancelled = false
    ;(async () => {
      const next = {}
      for (const r of missing) {
        const { data } = await supabase.storage.from('event-media').createSignedUrl(r.photo_storage_path, 3600)
        if (data?.signedUrl) next[r.photo_storage_path] = data.signedUrl
      }
      if (!cancelled && Object.keys(next).length) {
        setSignedPhotos(prev => ({ ...prev, ...next }))
      }
    })()
    return () => { cancelled = true }
  }, [rows])

  const setRowDraft = (id, patch) => setDraft(d => ({ ...d, [id]: { ...d[id], ...patch } }))
  const setRowBusy = (id, v) => setBusy(b => ({ ...b, [id]: v }))

  // Shared resolution writer. Status flips + GM notes + (optional) penalty
  // application + reporter notification all run from here.
  const resolve = async (row, nextStatus, applyPenalty = false) => {
    const d = draft[row.id] || {}
    setRowBusy(row.id, true)
    try {
      const penalty = applyPenalty ? Math.max(0, parseInt(d.penalty || 0, 10) || 0) : 0
      const update = {
        status: nextStatus,
        gm_notes: (d.notes ?? row.gm_notes ?? null) || null,
        penalty_points: penalty,
        resolved_at: new Date().toISOString(),
        resolved_by: user?.id,
      }
      const { error: upErr } = await supabase
        .from('war_crimes').update(update).eq('id', row.id)
      if (upErr) throw upErr

      // Apply penalty to accused's session_points (event-scoped). Only
      // runs on PENALIZE with a non-zero penalty + a known accused.
      if (applyPenalty && penalty > 0 && row.accused_id) {
        const { data: epRows } = await supabase
          .from('event_players').select('session_points')
          .eq('event_id', row.event_id).eq('user_id', row.accused_id).limit(1)
        const cur = epRows?.[0]?.session_points || 0
        await supabase
          .from('event_players')
          .update({ session_points: cur - penalty })
          .eq('event_id', row.event_id).eq('user_id', row.accused_id)
      }

      // Reporter-back notification. Server-side trigger handles the
      // INSERT fan-out; resolution is a single-recipient ping so we do
      // it from the client.
      const labelMap = {
        reviewed: 'Reviewed',
        dismissed: 'Dismissed',
        penalized: 'Penalized',
        escalated: 'Escalated',
      }
      await supabase.rpc('_notify_many', {
        p_event_id: row.event_id,
        p_recipient_ids: [row.reporter_id],
        p_type: 'war_crime_resolution',
        p_title: 'War Crime Report Resolved',
        p_body: `${labelMap[nextStatus] || nextStatus}: ${TYPE_LABELS[row.incident_type] || row.incident_type}`,
        p_metadata: { war_crime_id: row.id, status: nextStatus },
      })
    } catch (err) {
      console.warn('war crime resolve failed:', err)
    } finally {
      setRowBusy(row.id, false)
    }
  }

  // IGA action — append a note without changing terminal status. Pushes
  // status to 'reviewed' if still pending (signals "someone has eyes on
  // it") but does not dismiss or penalize.
  const igaSaveNote = async (row) => {
    const d = draft[row.id] || {}
    setRowBusy(row.id, true)
    try {
      const update = {
        gm_notes: (d.notes ?? row.gm_notes ?? null) || null,
        status: row.status === 'pending' ? 'reviewed' : row.status,
      }
      await supabase.from('war_crimes').update(update).eq('id', row.id)
    } finally {
      setRowBusy(row.id, false)
    }
  }

  return (
    <div style={S.panel}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <ShieldAlert size={16} style={{ color: '#ff6a3d' }} />
        <span style={{ ...S.label, color: '#ff6a3d', fontSize: 13, letterSpacing: '0.14em' }}>
          War Crime Reports
        </span>
        <span style={S.muted}>({rows.length})</span>
      </div>

      {rows.length === 0 ? (
        <p style={S.muted}>No war crime reports filed for this event.</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map(row => {
            const typeColor = TYPE_COLORS[row.incident_type] || '#8aa090'
            const statusColor = STATUS_COLORS[row.status] || '#8aa090'
            const reporterName = row.reporter?.callsign || row.reporter?.display_name || '—'
            const accusedName = row.accused?.callsign || row.accused?.display_name || null
            const photoUrl = row.photo_storage_path ? signedPhotos[row.photo_storage_path] : null
            const d = draft[row.id] || {}
            const rowBusy = !!busy[row.id]
            const isTerminal = ['dismissed','penalized','escalated'].includes(row.status)

            return (
              <div key={row.id} style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div>
                    <span style={S.pill(typeColor)}>{TYPE_LABELS[row.incident_type]}</span>
                    <span style={{ ...S.status(statusColor), marginLeft: 6 }}>{row.status}</span>
                  </div>
                  <span style={S.muted}>{new Date(row.created_at).toLocaleString()}</span>
                </div>

                <div style={S.body}>
                  <strong style={{ color: '#9adb6e' }}>{reporterName}</strong>
                  {accusedName ? (
                    <> → <strong style={{ color: '#ff6a3d' }}>{accusedName}</strong></>
                  ) : (
                    <span style={{ color: '#5a6a60' }}> → (unknown)</span>
                  )}
                </div>

                <p style={{ ...S.body, marginTop: 2 }}>{row.description}</p>

                {row.geo_lat != null && row.geo_lng != null && (
                  <a href={`https://maps.google.com/?q=${row.geo_lat},${row.geo_lng}`}
                     target="_blank" rel="noreferrer"
                     style={{ ...S.muted, color: '#4dd6c2', textDecoration: 'underline', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <MapPin size={11} />
                    {row.geo_lat.toFixed(5)}, {row.geo_lng.toFixed(5)}
                    {row.geo_accuracy_m != null && ` (±${row.geo_accuracy_m}m)`}
                  </a>
                )}

                {photoUrl && (
                  <button type="button" onClick={() => setLightbox(photoUrl)}
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', alignSelf: 'flex-start' }}>
                    <img src={photoUrl} alt="evidence"
                      style={{ width: 84, height: 84, objectFit: 'cover', border: '1px solid rgba(140,180,130,.3)' }} />
                  </button>
                )}

                {/* Resolution panel — visible unless the row is already terminal. */}
                {!isTerminal && (
                  <div style={{ marginTop: 8, paddingTop: 10, borderTop: '1px dashed rgba(140,180,130,.18)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div>
                      <label style={S.label}>GM Notes</label>
                      <textarea
                        value={d.notes ?? row.gm_notes ?? ''}
                        onChange={(e) => setRowDraft(row.id, { notes: e.target.value })}
                        placeholder="Resolution notes (visible to GMs and referees)..."
                        style={{ ...S.input, minHeight: 56, resize: 'vertical' }}
                      />
                    </div>
                    {mode === 'gm' && (
                      <div>
                        <label style={S.label}>Penalty Points (PENALIZE only)</label>
                        <input
                          type="number"
                          min="0"
                          value={d.penalty ?? row.penalty_points ?? ''}
                          onChange={(e) => setRowDraft(row.id, { penalty: e.target.value })}
                          style={{ ...S.input, width: 120 }}
                          placeholder="0"
                        />
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {mode === 'gm' ? (
                        <>
                          <button type="button" disabled={rowBusy}
                            onClick={() => resolve(row, 'dismissed', false)} style={S.btn('#8aa090')}>
                            <XIcon size={12} /> DISMISS
                          </button>
                          <button type="button" disabled={rowBusy}
                            onClick={() => resolve(row, 'reviewed', false)} style={S.btn('#ffcc33')}>
                            <FileCheck size={12} /> WARN
                          </button>
                          <button type="button" disabled={rowBusy || !row.accused_id}
                            title={!row.accused_id ? 'Penalize requires a known accused' : undefined}
                            onClick={() => resolve(row, 'penalized', true)} style={S.btn('#ff2a2a')}>
                            <AlertTriangle size={12} /> PENALIZE
                          </button>
                        </>
                      ) : (
                        <>
                          <button type="button" disabled={rowBusy}
                            onClick={() => igaSaveNote(row)} style={S.btn('#4dd6c2')}>
                            <MessageSquare size={12} /> {(d.notes && d.notes !== row.gm_notes) ? 'SAVE NOTE' : 'ACKNOWLEDGE'}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {isTerminal && row.gm_notes && (
                  <p style={{ ...S.muted, marginTop: 6 }}>
                    <span style={{ color: '#8aa090' }}>Notes:</span> {row.gm_notes}
                  </p>
                )}
              </div>
            )
          })}
        </div>
      )}

      {lightbox && (
        <div onClick={() => setLightbox(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.95)', zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'pointer' }}>
          <img src={lightbox} alt="evidence" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        </div>
      )}
    </div>
  )
}
